import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select } from '../components/Field.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { Money } from '../components/Money.js'
import { StatusPill } from '../components/StatusPill.js'

export const Route = createFileRoute('/_app/fees/categories')({
  component: CategoriesPage,
})

interface CategoryRow {
  id: string
  name: string
  amountInr: number
  termCount: number
  isActive: boolean
  createdAt: string
}

interface ClassRow {
  id: string
  name: string
}

interface ClassFeeRow {
  id: string
  classId: string
  feeCategoryId: string
  amountInr: number
}

function CategoriesPage() {
  const qc = useQueryClient()
  const categories = useQuery({
    queryKey: ['fee-categories'],
    queryFn: async () => {
      const res = await api<CategoryRow[]>('/api/v1/fee-categories')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [name, setName] = useState('')
  const [amountRupees, setAmountRupees] = useState('')
  const [termCount, setTermCount] = useState('1')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const res = await api<CategoryRow>('/api/v1/fee-categories', {
        method: 'POST',
        body: JSON.stringify({
          name,
          amountInr: Math.round(parseFloat(amountRupees) * 100),
          termCount: parseInt(termCount, 10) || 1,
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fee-categories'] })
      setName('')
      setAmountRupees('')
      setTermCount('1')
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    create.mutate()
  }

  const columns: Column<CategoryRow>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (c) => <span className="font-medium text-fg">{c.name}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (c) => <Money paise={c.amountInr} variant="data" />,
    },
    {
      key: 'terms',
      header: 'Terms',
      align: 'right',
      render: (c) => <span className="text-fg-muted">{c.termCount}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => (
        <StatusPill tone={c.isActive ? 'active' : 'cancelled'}>
          {c.isActive ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title="Fee categories"
        description="Reusable templates for tuition, transport, lab and other recurring fees."
        actions={
          <Link to="/fees" className="text-sm text-fg-muted hover:text-fg">
            ← All invoices
          </Link>
        }
      />

      <Section title="Add a category">
        <Card>
          <CardBody>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Name" required className="md:col-span-1">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Tuition · Term 1"
                />
              </Field>
              <Field label="Amount (₹)" required>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amountRupees}
                  onChange={(e) => setAmountRupees(e.target.value)}
                  required
                  numeric
                  prefix="₹"
                  placeholder="9999.00"
                />
              </Field>
              <Field label="Terms" hint="Number of installments">
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={termCount}
                  onChange={(e) => setTermCount(e.target.value)}
                  numeric
                />
              </Field>
            </form>
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            {error ? <p className="text-sm text-danger">{error}</p> : <span />}
            <Button
              onClick={() => create.mutate()}
              disabled={!name || !amountRupees || create.isPending}
              loading={create.isPending}
            >
              Add category
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section title="All categories" description={`${categories.data?.length ?? 0} total`}>
        <DataTable
          columns={columns}
          rows={categories.data ?? []}
          rowKey={(c) => c.id}
          loading={categories.isLoading}
          empty="No categories yet. Add one above to speed up invoice creation."
        />
      </Section>

      <ClassFeesSection categories={categories.data ?? []} />
    </div>
  )
}

function ClassFeesSection({ categories }: { categories: CategoryRow[] }) {
  const qc = useQueryClient()
  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })
  const classFees = useQuery({
    queryKey: ['class-fees'],
    queryFn: async () => {
      const res = await api<ClassFeeRow[]>('/api/v1/class-fees')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [classId, setClassId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [amountRupees, setAmountRupees] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const res = await api<ClassFeeRow>('/api/v1/class-fees', {
        method: 'POST',
        body: JSON.stringify({
          classId,
          feeCategoryId: categoryId,
          amountInr: Math.round(parseFloat(amountRupees) * 100),
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['class-fees'] })
      setClassId('')
      setCategoryId('')
      setAmountRupees('')
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api(`/api/v1/class-fees/${id}`, { method: 'DELETE' })
      if ('error' in res) throw new Error(res.error.message)
      return id
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['class-fees'] }),
  })

  const className = (id: string) => classes.data?.find((c) => c.id === id)?.name ?? '—'
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? '—'

  const columns: Column<ClassFeeRow>[] = [
    { key: 'class', header: 'Class', render: (f) => <span className="font-medium text-fg">{className(f.classId)}</span> },
    { key: 'category', header: 'Category', render: (f) => <span className="text-fg-muted">{categoryName(f.feeCategoryId)}</span> },
    { key: 'amount', header: 'Amount', align: 'right', render: (f) => <Money paise={f.amountInr} variant="data" /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (f) => (
        <Button size="sm" variant="ghost" onClick={() => remove.mutate(f.id)} disabled={remove.isPending}>
          Remove
        </Button>
      ),
    },
  ]

  return (
    <>
      <Section
        title="Add a class fee"
        description="Set the standard amount for a class and category. It auto-fills on new invoices, still overridable."
      >
        <Card>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Class" required>
                <Select value={classId} onChange={(e) => setClassId(e.target.value)} required>
                  <option value="">Select a class…</option>
                  {classes.data?.map((cl) => (
                    <option key={cl.id} value={cl.id}>
                      {cl.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Category" required>
                <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
                  <option value="">Select a category…</option>
                  {categories
                    .filter((c) => c.isActive)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
              </Field>
              <Field label="Amount (₹)" required>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amountRupees}
                  onChange={(e) => setAmountRupees(e.target.value)}
                  required
                  numeric
                  prefix="₹"
                  placeholder="12000.00"
                />
              </Field>
            </div>
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            {error ? <p className="text-sm text-danger">{error}</p> : <span />}
            <Button
              onClick={() => {
                setError(null)
                create.mutate()
              }}
              disabled={!classId || !categoryId || !amountRupees || create.isPending}
              loading={create.isPending}
            >
              Add class fee
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section title="All class fees" description={`${classFees.data?.length ?? 0} total`}>
        <DataTable
          columns={columns}
          rows={classFees.data ?? []}
          rowKey={(f) => f.id}
          loading={classFees.isLoading}
          empty="No class fees yet. Invoices fall back to the category amount."
        />
      </Section>
    </>
  )
}
